import React from 'react';

import { OutboundCallFieldsFragment } from '~/api/graphql';
import { useFloatingSelection } from '~/components/list/use-floating-selection-bar';
import { useLibraryBulkArchive } from '~/components/list/use-library-bulk-archive';
import { partitionOutboundCallSelection } from './outbound-call';

/**
 * Selection + bulk cancel / archive wiring for the outbound Calls list.
 * Keeps fetcher/dialog state out of the page component.
 *
 * List rows come from searchOutboundCalls (Apollo), so after bulk success we
 * refetch rather than patching local useState — same as workspace archive/restore.
 */
export function useOutboundCallsBulkActions(params: {
  calls: OutboundCallFieldsFragment[];
  refetch: () => void | Promise<unknown>;
}) {
  const { calls, refetch } = params;
  const selection = useFloatingSelection();
  const clearSelection = selection.clear;

  const { cancellableIds, archivableIds } = React.useMemo(
    () => partitionOutboundCallSelection(calls, selection.selected),
    [calls, selection.selected]
  );

  const onCancelled = React.useCallback(() => {
    clearSelection();
    void refetch();
  }, [clearSelection, refetch]);

  const onArchived = React.useCallback(() => {
    clearSelection();
    void refetch();
  }, [clearSelection, refetch]);

  const cancel = useLibraryBulkArchive({
    actionPath: '/app/calls/outbound/cancel',
    payloadKey: 'outboundCallIds',
    onArchived: onCancelled
  });

  const archive = useLibraryBulkArchive({
    actionPath: '/app/calls/outbound/archive',
    payloadKey: 'outboundCallIds',
    onArchived
  });

  const submitCancel = cancel.archive;
  const submitArchive = archive.archive;
  const clearCancelFailedIds = cancel.clearFailedIds;
  const clearArchiveFailedIds = archive.clearFailedIds;

  const confirmCancel = React.useCallback(() => {
    if (cancellableIds.length === 0) {
      return;
    }
    submitCancel(cancellableIds);
  }, [cancellableIds, submitCancel]);

  const confirmArchive = React.useCallback(() => {
    if (archivableIds.length === 0) {
      return;
    }
    submitArchive(archivableIds);
  }, [archivableIds, submitArchive]);

  const bulkError = React.useMemo(() => {
    if (cancel.failedIds.length > 0) {
      return `Could not cancel ${cancel.failedIds.length} ${
        cancel.failedIds.length === 1 ? 'call' : 'calls'
      }. Try again.`;
    }
    if (archive.failedIds.length > 0) {
      return `Could not archive ${archive.failedIds.length} ${
        archive.failedIds.length === 1 ? 'call' : 'calls'
      }. Try again.`;
    }
    return null;
  }, [archive.failedIds, cancel.failedIds]);

  const dismissBulkError = React.useCallback(() => {
    clearCancelFailedIds();
    clearArchiveFailedIds();
  }, [clearArchiveFailedIds, clearCancelFailedIds]);

  return {
    selection,
    cancellableIds,
    archivableIds,
    cancel,
    archive,
    confirmCancel,
    confirmArchive,
    bulkError,
    dismissBulkError
  };
}
